import { request } from '@umijs/max';
import type { RespGet, RespPage } from '@/services/resp';
import type {
  SecurityOverview,
  LicenseScore,
  LicenseScoreDetail,
  DeviceIpTimeline,
  SecurityAlert,
  AlertListParams,
  UnreadAlertCount,
  SecurityAlertDetail,
  AcknowledgeResponse,
  FreezeRequest,
  FreezeResponse,
  ResolveFalsePositiveRequest,
  ResolveFalsePositiveResponse,
  ManualFreezeRequest,
  ManualFreezeResponse,
  UnfreezeRequest,
  UnfreezeResponse,
  AddNoteRequest,
  AddNoteResponse,
  RuleStatItem,
  LicenseSecurityConfig,
  RuleEffectivenessItem,
} from './typing';

const API_PREFIX = '/api/v1/pam/admin/security';

/** 1. 安全总览指标 GET /api/v1/pam/admin/security/dashboard/overview */
export async function getSecurityOverview() {
  return request<RespGet<SecurityOverview>>(`${API_PREFIX}/dashboard/overview`, {
    method: 'GET',
  });
}

/** 2. 许可证安全评分列表 GET /api/v1/pam/admin/security/dashboard/license-scores */
export async function getLicenseScores(params?: {
  current?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  minScore?: number;
  maxScore?: number;
  licenseName?: string;
}) {
  return request<RespPage<LicenseScore>>(`${API_PREFIX}/dashboard/license-scores`, {
    method: 'GET',
    params,
  });
}

/** 3. 单个许可证安全评分详情 GET /api/v1/pam/admin/security/dashboard/license-scores/:licenseId */
export async function getLicenseScoreDetail(licenseId: string) {
  return request<RespGet<LicenseScoreDetail>>(
    `${API_PREFIX}/dashboard/license-scores/${licenseId}`,
    {
      method: 'GET',
    },
  );
}

/** 4. 设备心跳 IP 时间线 GET /api/v1/pam/admin/security/devices/:deviceId/ip-timeline */
export async function getDeviceIpTimeline(deviceId: string, days?: number) {
  return request<RespGet<DeviceIpTimeline>>(`${API_PREFIX}/devices/${deviceId}/ip-timeline`, {
    method: 'GET',
    params: { days },
  });
}

/** 5. 告警列表 GET /api/v1/pam/admin/security/alerts */
export async function getAlertList(params?: AlertListParams) {
  return request<RespPage<SecurityAlert>>(`${API_PREFIX}/alerts`, {
    method: 'GET',
    params,
  });
}

/** 6. 未处理告警数 GET /api/v1/pam/admin/security/alerts/unread-count */
export async function getUnreadAlertCount() {
  return request<RespGet<UnreadAlertCount>>(`${API_PREFIX}/alerts/unread-count`, {
    method: 'GET',
  });
}

/** 7. 安全事件详情 GET /api/v1/pam/admin/security/alerts/:id */
export async function getAlertDetail(id: number) {
  return request<RespGet<SecurityAlertDetail>>(`${API_PREFIX}/alerts/${id}`, {
    method: 'GET',
  });
}

/** 8. 确认告警 POST /api/v1/pam/admin/security/alerts/:id/acknowledge */
export async function acknowledgeAlert(id: number) {
  return request<RespGet<AcknowledgeResponse>>(`${API_PREFIX}/alerts/${id}/acknowledge`, {
    method: 'POST',
  });
}

/** 9. 确认盗版冻结 POST /api/v1/pam/admin/security/alerts/:id/freeze */
export async function freezeByAlert(id: number, data: FreezeRequest) {
  return request<RespGet<FreezeResponse>>(`${API_PREFIX}/alerts/${id}/freeze`, {
    method: 'POST',
    data,
  });
}

/** 10. 确认误报恢复 POST /api/v1/pam/admin/security/alerts/:id/resolve-false-positive */
export async function resolveFalsePositive(id: number, data: ResolveFalsePositiveRequest) {
  return request<RespGet<ResolveFalsePositiveResponse>>(
    `${API_PREFIX}/alerts/${id}/resolve-false-positive`,
    {
      method: 'POST',
      data,
    },
  );
}

/** 11. 添加调查备注 POST /api/v1/pam/admin/security/alerts/:id/notes */
export async function addInvestigationNote(id: number, data: AddNoteRequest) {
  return request<RespGet<AddNoteResponse>>(`${API_PREFIX}/alerts/${id}/notes`, {
    method: 'POST',
    data,
  });
}

/** 12. 手动冻结许可证 POST /api/v1/pam/admin/security/licenses/:licenseId/freeze */
export async function freezeLicense(licenseId: string, data: ManualFreezeRequest) {
  return request<RespGet<ManualFreezeResponse>>(`${API_PREFIX}/licenses/${licenseId}/freeze`, {
    method: 'POST',
    data,
  });
}

/** 13. 解冻许可证 POST /api/v1/pam/admin/security/licenses/:licenseId/unfreeze */
export async function unfreezeLicense(licenseId: string, data: UnfreezeRequest) {
  return request<RespGet<UnfreezeResponse>>(`${API_PREFIX}/licenses/${licenseId}/unfreeze`, {
    method: 'POST',
    data,
  });
}

/** F22: 规则命中统计 GET /api/v1/pam/admin/security/rule-stats */
export async function getRuleStats(days?: number) {
  return request<RespGet<RuleStatItem[]>>(`${API_PREFIX}/rule-stats`, {
    method: 'GET',
    params: { days },
  });
}

/** F23: 获取许可证安全配置 GET /api/v1/pam/admin/security/licenses/:licenseId/security-config */
export async function getLicenseSecurityConfig(licenseId: string) {
  return request<RespGet<LicenseSecurityConfig>>(
    `${API_PREFIX}/licenses/${licenseId}/security-config`,
    { method: 'GET' },
  );
}

/** F23: 保存许可证安全配置 PUT /api/v1/pam/admin/security/licenses/:licenseId/security-config */
export async function updateLicenseSecurityConfig(
  licenseId: string,
  data: Partial<LicenseSecurityConfig>,
) {
  return request<RespGet<LicenseSecurityConfig>>(
    `${API_PREFIX}/licenses/${licenseId}/security-config`,
    { method: 'PUT', data },
  );
}

/** F24: 规则效果趋势 GET /api/v1/pam/admin/security/rule-effectiveness */
export async function getRuleEffectiveness(days?: number) {
  return request<RespGet<RuleEffectivenessItem[]>>(`${API_PREFIX}/rule-effectiveness`, {
    method: 'GET',
    params: { days },
  });
}
